import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Home, Users, Briefcase, Clock, FileText, Bell, Settings, LogOut, Menu, X,
  ChevronRight, ChevronDown, Plus, CheckCircle, AlertCircle, MapPin, Calendar, User,
  ClipboardList, BarChart3, Play, Square, RefreshCw, Search, Filter, Eye,
  Truck, Camera, Upload, Image, Wrench, Shield, FileImage, Trash2, Save,
  Building2, Sliders, Key, MessageSquare, Send, Car, Fuel, Video, HelpCircle,
  ChevronLeft, Download, Edit3, Check, ArrowLeft, Edit2, UserCheck, Archive, Receipt, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Badge, Button, Card, Input, Modal, Select, Spinner, Textarea, FileUploadInput } from "../../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SWMSSignView = ({ job, swms, signed, signature, onSign, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleSign = async () => {
    if (!acknowledged) {
      alert("Please confirm you have read and understood the SWMS");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/swms/${swms.id}/sign`);
      alert("SWMS signed successfully! You can now clock in for this job.");
      onSign();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to sign SWMS");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
        <h3 className="text-lg font-bold text-red-800 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          SWMS - Safe Work Method Statement
        </h3>
        <p className="text-sm text-red-700 mt-1">
          You must read and sign this SWMS before working on "{job.title}"
        </p>
      </div>

      <Card>
        <h4 className="font-bold mb-2">{swms.name}</h4>
        {swms.description && <p className="text-sm text-slate-600 mb-4">{swms.description}</p>}
        <p className="text-xs text-slate-400 mb-4">Version: {swms.version}</p>
        
        <Button
          variant="secondary"
          onClick={() => openFile(swms.file_url, 'SWMS')}
          className="w-full mb-4"
        >
          <Eye className="w-5 h-5 mr-2" />
          Open SWMS Document (Read Carefully)
        </Button>
      </Card>

      {signed ? (
        <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="w-5 h-5" />
            <span className="font-bold">You have signed this SWMS</span>
          </div>
          <p className="text-sm text-green-700 mt-1">
            Signed on {new Date(signature.signed_at).toLocaleDateString()} at {new Date(signature.signed_at).toLocaleTimeString()}
          </p>
        </div>
      ) : (
        <>
          <label className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="w-6 h-6 mt-0.5"
            />
            <span className="text-sm">
              <strong className="text-amber-900">I confirm that I have:</strong>
              <ul className="list-disc list-inside text-amber-800 mt-2 space-y-1">
                <li>Read the entire Safe Work Method Statement</li>
                <li>Understood all hazards and control measures</li>
                <li>Will follow all safety procedures outlined</li>
                <li>Will report any issues or concerns immediately</li>
              </ul>
            </span>
          </label>

          <Button
            onClick={handleSign}
            disabled={loading || !acknowledged}
            className="w-full"
            data-testid="sign-swms-btn"
          >
            {loading ? <Spinner size="sm" /> : "Sign SWMS"}
          </Button>
        </>
      )}

      <Button variant="ghost" onClick={onBack}>Back to Job Details</Button>
    </div>
  );
};

// SWMS Manage View (for admins to upload/manage SWMS and see signatures)

export default SWMSSignView;
